/**
 * favoritesService.js - Servicio frontend para favoritos del directorio
 *
 * Comunicación con /api/directory/favorites
 */
import { API_URL } from '../../services/api/config';
import { getAuthHeaders, fetchWrapper } from '../../services/api/utils/fetchWrapper';

const BASE_URL = `${API_URL}/api/directory/favorites`;

/**
 * Obtener mis favoritos
 * @param {string} perfilId - ID del perfil (opcional)
 * @param {boolean} includeMetadata - Si incluir count, limit y plan en la respuesta
 * @returns {Promise<Array|Object>} Lista de favoritos o { data, count, limit, plan }
 */
export async function getFavorites(perfilId = null, includeMetadata = false) {
  const params = new URLSearchParams();
  if (perfilId) params.append('perfilId', perfilId);

  const queryString = params.toString();
  const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL;

  const headers = await getAuthHeaders();
  const response = await fetchWrapper(url, { headers });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al obtener favoritos');
  }

  const result = await response.json();

  if (includeMetadata) {
    return {
      data: result.data || [],
      count: result.count || 0,
      limit: result.limit || 3,
      plan: result.plan || 'light'
    };
  }

  return result.data || [];
}

/**
 * Agregar favorito
 * @param {string} placeId - ID del lugar
 * @param {string} perfilId - ID del perfil (opcional, default: 'general')
 * @param {string} perfilTipo - Tipo de perfil ('mascota', 'objeto', 'general')
 * @returns {Promise<Object>} Favorito creado
 */
export async function addFavorite(placeId, perfilId = 'general', perfilTipo = 'general') {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(BASE_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({ placeId, perfilId, perfilTipo })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al agregar favorito');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Quitar favorito
 * @param {string} id - ID del favorito
 * @returns {Promise<boolean>} true si se eliminó
 */
export async function removeFavorite(id) {
  const headers = await getAuthHeaders();
  const response = await fetchWrapper(`${BASE_URL}/${id}`, {
    method: 'DELETE',
    headers
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al quitar favorito');
  }

  return true;
}

/**
 * Verificar si un lugar es favorito
 * @param {string} placeId - ID del lugar
 * @param {string} perfilId - ID del perfil (opcional)
 * @returns {Promise<Object>} { isFavorito, favoritoId }
 */
export async function checkFavorite(placeId, perfilId = null) {
  const params = new URLSearchParams();
  if (perfilId) params.append('perfilId', perfilId);

  const queryString = params.toString();
  const url = queryString
    ? `${BASE_URL}/check/${placeId}?${queryString}`
    : `${BASE_URL}/check/${placeId}`;

  const headers = await getAuthHeaders();
  const response = await fetchWrapper(url, { headers });

  if (!response.ok) {
    // Si falla, asumir que no es favorito
    return { isFavorito: false, favoritoId: null };
  }

  const data = await response.json();
  return data.data;
}

export default {
  getFavorites,
  addFavorite,
  removeFavorite,
  checkFavorite
};
